// Workbook-derived presentation copy only; calculations remain in the API.
export const workbookCopy = {
  "tax-free-cash": {
    "source": "Tax Free Cash workbook - historical 2018/2019 basis",
    "introduction": "Compare the tax-free cash available in the existing plan with the immediate tax-free cash available after transfer, and review the wording for the suitability report.",
    "labels": {
      "fundValueAtADay": "Fund value at A-Day (£)",
      "taxFreeCashAtADay": "Tax-free cash at A-Day (£)",
      "transferValueToday": "Transfer value today (£)",
      "upliftedTaxFreeCashAtADay": "A-Day tax-free cash increased by 20%",
      "growthComponent": "25% of adjusted fund growth since A-Day",
      "oldPlanTaxFreeCash": "Total tax-free cash available in the old plan now",
      "oldPlanTaxFreeCashRatio": "Old-plan tax-free cash as a percentage of today’s fund",
      "newPlanTaxFreeCash": "Immediate tax-free cash in the new pension (25%)",
      "taxFreeCashReduction": "Old-plan minus new-plan tax-free cash"
    },
    "fieldHelp": {
      "fundValueAtADay": "Enter the value at A-Day, separately from today’s fund and transfer values.",
      "taxFreeCashAtADay": "The historical workbook increases this by a fixed 20%; this is a fixed part of its formula.",
      "fundValueToday": "Used for the existing-plan cash comparison and percentage.",
      "transferValueToday": "The new-plan illustration uses 25% of this transfer value."
    },
    "notes": [
      "The workbook describes the figures as approximations illustrating nominal differences in tax-free cash between the old and new pensions at the point of transfer. Its 2018/2019 rules are historical; confirm current eligibility and treatment separately.",
      "The calculation adds the fixed 20% uplift to A-Day tax-free cash and 25% of adjusted fund growth since A-Day.",
      "Including these figures or this wording in a suitability report does not justify losing tax-free cash. Explain why any loss is acceptable to the client, the other benefits being considered, and your discussions with the client."
    ]
  },
  "pension-shortfall": {
    "source": "Pension Shortfall Modeller workbook",
    "introduction": "Illustrate the fund value at the client’s intended retirement age and the annual income represented by 4% of that fund.",
    "labels": {
      "grossMonthlyPremium": "Gross monthly pension premiums (£)",
      "annualGrowthRatePercent": "Assumed annual growth rate (%)",
      "projectedRetirementFund": "Projected fund value by retirement age",
      "illustrativeAnnualIncome": "Illustrative annual retirement income at 4%",
      "annualGrossContribution": "Gross premiums per year",
      "yearsToRetirement": "Term to intended retirement (years)"
    },
    "fieldHelp": {
      "retirementAge": "The client’s intended retirement age.",
      "currentFundValue": "The client’s total current pension fund value.",
      "grossMonthlyPremium": "Use the gross monthly premium shown in the pension information.",
      "annualGrowthRatePercent": "Workbook guidance: Conservative 1%, Balanced 2%, Moderate 3%, Dynamic 4%, Adventurous 5%. These are illustration assumptions, not forecasts or guarantees."
    },
    "notes": [
      "The original describes income as a 4% annuity illustration or withdrawals of 4% of the fund per year. The calculator simply divides the projected fund by 25; it does not source an annuity or assess whether withdrawals are sustainable.",
      "Although premiums are entered monthly, the working workbook formula aggregates them into annual end-of-year contributions. It does not use the separate monthly test calculation.",
      "These are approximate illustrations based on assumptions. No target retirement-income input is included, so the results do not calculate an actual income shortfall."
    ],
    "growthGuide": [
      [
        "Conservative",
        "1%"
      ],
      [
        "Balanced",
        "2%"
      ],
      [
        "Moderate",
        "3%"
      ],
      [
        "Dynamic",
        "4%"
      ],
      [
        "Adventurous",
        "5%"
      ]
    ]
  },
  "critical-yield": {
    "source": "Critical Yield Calculator workbook",
    "introduction": "Compare the old pension’s implied growth and reduction in yield with the additional yield required in the new pension to match the old provider’s projected figure.",
    "labels": {
      "oldFundValue": "Old pension fund value (£)",
      "illustrationDate": "Date the old illustration was produced",
      "retirementDate": "Retirement date on the old illustration",
      "oldGrossProjectionRatePercent": "Old provider’s gross projection rate (%)",
      "oldProjectedFundValue": "Fund value projected by the old provider (£)",
      "transferValue": "Transfer value of the old pension (£)",
      "newPlanReductionInYieldPercent": "Proposed new pension reduction in yield (%)",
      "oldGrossProjectionRate": "Old pension gross projection rate",
      "oldPlanNetAnnualGrowthRate": "Implied actual annual growth in the old plan",
      "oldPlanReductionInYield": "Implied overall reduction in yield in the old plan",
      "requiredTransferNetAnnualGrowthRate": "Required net growth from the transfer value",
      "additionalYieldRequired": "Additional yield required to match the old projection"
    },
    "fieldHelp": {
      "oldFundValue": "Use the fund value, not the transfer value.",
      "oldGrossProjectionRatePercent": "Enter the rate used by the old provider, for example 5 for 5% or 8.5 for 8.5%.",
      "oldProjectedFundValue": "Enter the projected fund value on the old provider’s illustration.",
      "transferValue": "Keep this separate from the fund value. A lower transfer value can reflect a transfer penalty.",
      "newPlanReductionInYieldPercent": "Use the RIY for the proposed new pension; for example 1.3 means 1.3%."
    },
    "notes": [
      "Old pension: the workbook compares the gross projection rate with the implied annual growth to show the overall reduction in yield under its assumptions.",
      "New pension: the output is the additional yield required to match the old pension’s projected figure. It takes account of a lower transfer value where a transfer penalty applies.",
      "The workbook says this is the figure to explain in the letter to the client. It describes a negative additional-yield result as the new plan being cheaper under the model’s assumptions; this alone is not a suitability conclusion.",
      "The result is additional yield relative to the old gross rate, not the total gross growth required. Contributions and separate charge schedules are not modelled."
    ]
  },
  "annuity-risk": {
    "source": "Annuity & Risk of Ruin workbook - Assureweb rates dated 11 March 2025",
    "introduction": "Review the considerations and consequences of a pension withdrawal: illustrative annuity income before and after the withdrawal, fund exhaustion, tax and two further-withdrawal illustrations.",
    "labels": {
      "withdrawalAmount": "Total amount to withdraw now (£)",
      "taxFreeCashIncluded": "Tax-free cash included in this withdrawal (£)",
      "otherAnnualIncome": "Other earned income in this tax year (£)",
      "expectedDeathAge": "Stress-tested expected age at death from the fact find",
      "annuityBeforeWithdrawal": "Approximate annuity income before withdrawal (a year)",
      "annuityAfterWithdrawal": "Approximate annuity income after withdrawal (a year)",
      "exhaustionYears": "Years until the fund is exhausted at this annual withdrawal",
      "exhaustionAge": "Approximate age when the fund is exhausted",
      "totalIncomeTax": "Total tax on other income and this taxable withdrawal",
      "additionalWithdrawalTax": "Extra tax resulting from this withdrawal",
      "guardrailIncomeThreePercent": "Guardrail income 1: 3% of the remaining fund a year",
      "guardrailIncomeToExpectedAge": "Guardrail income 2: annual amount to expected age at death"
    },
    "fieldHelp": {
      "taxFreeCashIncluded": "This is part of the total withdrawal, not an additional withdrawal. Enter zero if none is included.",
      "otherAnnualIncome": "Use other income for the selected tax year, excluding this pension withdrawal.",
      "expectedDeathAge": "Use the stress-tested age recorded in the fact find. The workbook’s example ages are historical assumptions, not a personalised life-expectancy assessment.",
      "taxYear": "The original workbook is labelled 2025/2026. The application uses the selected supported year and the corrected tax comparison."
    },
    "notes": [
      "Historical annuity quotation basis: 1% fee, single life, no spouse, 10-year guarantee, monthly in advance, £100,000 reference fund, SN4 7DU postcode, nonsmoker and no health issues. Rates were taken from Assureweb on 11 March 2025; these are not current quotations.",
      "The workbook notes that only Aviva quoted at age 100, so the application uses these reference rates for ages 55–99 only.",
      "The workbook suggests stress-test examples of 87 for a man or 90 for a woman, or 83/86 where lower life expectancy is expected. These are retained as historical workbook examples only; the workbook’s ONS wording is not a verified current mortality assessment.",
      "If the same amount is withdrawn every year, the exhaustion calculation uses the original fund, including today’s withdrawal, and assumes no growth, fees or inflation. No probability of ruin is calculated.",
      "Guardrail 1 applies 3% to the fund left after today’s withdrawal. Guardrail 2 spreads that remaining fund over the years to the entered expected age at death. These are illustrations, not guarantees of a safe withdrawal rate.",
      "The workbook says the shaded calculation figures should be explained in the Flexi Access Drawdown Letter. Its suggested client discussion points are listed below; they are not a completed client declaration or a withdrawal authorisation."
    ],
    "discussion": [
      "Clarify the exact income-tax position, including whether accountant input is needed.",
      "Discuss whether spreading taxable withdrawals across different tax years could change the tax outcome.",
      "Check whether taking taxable pension income triggers the Money Purchase Annual Allowance and restricts future contributions. Verify the applicable rules rather than relying on the workbook’s historical £10,000/£60,000 figures.",
      "Consider an income for life through an annuity alongside the flexibility of withdrawals.",
      "Discuss the risk that the pension fund could run out during the client’s lifetime.",
      "Give the client an opportunity to ask further questions and record their decision separately. The workbook’s optional signature wording is not treated as consent in this calculator."
    ]
  },
  "growth-required": {
    "source": "Growth Required Calculator workbook",
    "introduction": "Calculate the growth per year needed for the transfer value to reach the figure guaranteed by the old provider or its capitalised value.",
    "labels": {
      "transferValue": "Transfer value of the old pension (£)",
      "illustrationDate": "Date the old illustration was produced",
      "retirementDate": "Retirement date on the old illustration",
      "targetFundValue": "Guaranteed figure or capitalised value (£)",
      "newPlanReductionInYieldPercent": "New plan reduction in yield (%)",
      "requiredNetAnnualGrowthRate": "Required net growth per year",
      "requiredGrossAnnualGrowthRate": "Growth per year including the new plan RIY"
    },
    "fieldHelp": {
      "transferValue": "Enter the transfer value, not the annual pension income.",
      "targetFundValue": "Enter the figure guaranteed by the old provider or its capitalised lump-sum value. Do not enter an annual income.",
      "newPlanReductionInYieldPercent": "Enter percentage points, for example 1.3 for 1.3%. The workbook adds this to the required net growth."
    },
    "notes": [
      "The workbook labels the result “Growth Per Year (Yield Figure)”. This calculator shows both the required net growth and the total gross growth after adding RIY.",
      "The term runs from the old illustration date to the retirement date, using elapsed days divided by 365.25.",
      "This is total required growth, rather than the additional yield comparison used in Critical Yield. It assumes a starting lump sum with no future contributions and adds RIY arithmetically.",
      "If the target is below the transfer value, a negative calculated growth requirement is retained."
    ]
  },
  "weighted-average": {
    "source": "Weighted Average Calculator workbook",
    "introduction": "Combine the projection rates or reduction-in-yield figures for the funds using the percentage invested in each fund.",
    "labels": {
      "weightedAverageRatePercent": "Weighted average projection rate / RIY (%)",
      "totalAllocationPercent": "Total percentage invested"
    },
    "fieldHelp": {
      "ratePercent": "Use the same measure for every fund: either projection rates or RIY figures. Enter 5 for 5%.",
      "allocationPercent": "Percentage of the total investment allocated to this fund. All funds must total 100%."
    },
    "notes": [
      "The original columns are Funds, Projection Rate/RIY, % Invested and Weighted rate, with up to ten funds.",
      "The workbook states that the percentages invested must total 100%. The application validates this and does not rescale incomplete allocations.",
      "Each contribution is rate × percentage invested ÷ 100; the weighted average is the sum of those contributions. Negative rates are retained."
    ]
  },
  "gar": {
    "source": "GAR Calculation workbook - plan-specific guaranteed cash sum comparison",
    "introduction": "Compare the existing scheme’s guaranteed annuity income with adviser-sourced open-market income after the selected tax-free cash, including escalation and spouse benefits.",
    "notes": [
      "The original calculation is headed “Where Guaranteed Cash Sum Applies”. Confirm the provider’s guaranteed cash-sum basis, or transfer value if higher, rather than assuming the transferable fund and guaranteed fund are identical.",
      "The source separates Non Pro, Pre97 and Post97 benefits, with a fund value, GAR, open-market rate, spouse pension and escalation rate for each tranche. Enter the tranches and rates that actually apply to this plan and age.",
      "The original “annuity rate from FSA website at retirement” label is historical. The adviser must obtain and record an appropriate open-market quote; the application does not provide current sourcing or an automatic age-based GAR rate.",
      "Source outputs include total transfer value, tax-free cash, GAR and open-market income after cash, GAR income after 5/10/15 years, spouse pension at outset, and the fund/growth needed to match the guarantee.",
      "The after-5/10/15-year figures are annual incomes at those anniversaries, not cumulative payments. Escalation uses the fixed scenario rates entered; it is not an inflation forecast.",
      "Matching-fund and growth estimates are available only when quotation bases are confirmed as comparable. A higher starting income does not, by itself, establish GAR value or suitability."
    ]
  },
  "reduction-in-yield": {
    "source": "User-specified fee formula - no corresponding Excel workbook supplied",
    "introduction": "Estimate annual reduction in yield from product fees, fund fees and adviser ongoing fees, plus the initial fee spread over the investment term.",
    "labels": {
      "ongoingFeesPercent": "Total annual ongoing fees (%)",
      "annualisedInitialFeePercent": "Initial fee spread over the term (% a year)",
      "reductionInYieldPercent": "Estimated annual reduction in yield (%)",
      "reductionInYieldRate": "Estimated annual reduction in yield (same result)"
    },
    "fieldHelp": {
      "productFeePercent": "Annual percentage, not a pound charge. Enter zero if none applies.",
      "fundFeePercent": "Annual percentage, using the same basis as the other ongoing fees.",
      "adviserOngoingFeePercent": "Annual adviser fee percentage, excluding the one-off initial fee.",
      "initialFeePercent": "One-off initial percentage fee. It is divided by the investment term, not added as an annual fee.",
      "investmentTermYears": "A positive term in years; fractional years are supported."
    },
    "notes": [
      "Product fee + fund fee + adviser ongoing fee + (initial fee ÷ investment term). All fee inputs are percentage points: 0.5 means 0.5%.",
      "The first three fees are annual. The initial fee is a one-off percentage spread evenly over the term. This is a simple arithmetic estimate without compounded charges or investment-growth modelling."
    ]
  }
};
